import { useEffect, useState } from "react";
import {
  Bot, Send, UserRound, Headset, Loader2, Save, Sparkles, BookOpen, Plus, Trash2,
  MessageSquare, Search, Lightbulb, UserCog, Globe, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { KnowledgeDialog } from "@/components/KnowledgeDialog";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "pochi secondi fa";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  return `${Math.floor(diff / 86400)} g fa`;
}

export default function AIAgent() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [knowledge, setKnowledge] = useState([]);
  const [refreshingId, setRefreshingId] = useState(null);
  const [kbOpen, setKbOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activePhone, setActivePhone] = useState(null);
  const [thread, setThread] = useState([]);
  const [phone, setPhone] = useState("+39333444555");
  const [msg, setMsg] = useState("Quanto costa la spedizione?");
  const [sending, setSending] = useState(false);
  const [convSearch, setConvSearch] = useState("");
  const [suggesting, setSuggesting] = useState(false);

  const load = async () => {
    const [s, kb, conv] = await Promise.all([
      api.get("/settings"), api.get("/knowledge"), api.get("/whatsapp/conversations"),
    ]);
    setSettings(s.data);
    setKnowledge(kb.data);
    setConversations(conv.data);
  };
  useEffect(() => {
    load().then(() => {
      const p = new URLSearchParams(window.location.search).get("phone");
      if (p) { setPhone(p); openThread(p); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openThread = async (p) => {
    setActivePhone(p);
    setPhone(p);
    const { data } = await api.get(`/whatsapp/conversations/${encodeURIComponent(p)}`);
    setThread(data);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        ai_enabled: settings.ai_enabled,
        notify_operator: settings.notify_operator,
        auto_faq: settings.auto_faq,
        ai_business_name: settings.ai_business_name,
        ai_business_info: settings.ai_business_info,
      });
      setSettings(data);
      toast.success("Configurazione agente salvata");
    } catch {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const deleteKnowledge = async (id) => {
    await api.delete(`/knowledge/${id}`);
    toast.success("Voce eliminata");
    load();
  };
  const refreshSource = async (id) => {
    setRefreshingId(id);
    try {
      await api.post(`/knowledge/${id}/refresh`);
      toast.success("Sito ri-letto e aggiornato");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Aggiornamento non riuscito");
    } finally {
      setRefreshingId(null);
    }
  };

  const send = async () => {
    if (!phone || !msg) { toast.error("Inserisci numero e messaggio"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/whatsapp/inbound", { phone, message: msg });
      if (data.ai_reply) toast.success(data.needs_human ? "Richiesta inoltrata a un operatore" : "Risposta IA inviata");
      else toast.info("Messaggio gestito (STOP/START)");
      setMsg("");
      await load();
      await openThread(phone);
    } catch {
      toast.error("Errore durante l'invio");
    } finally {
      setSending(false);
    }
  };

  const sendOperator = async () => {
    if (!phone || !msg) { toast.error("Seleziona una chat e scrivi la risposta"); return; }
    setSending(true);
    try {
      await api.post(`/whatsapp/conversations/${encodeURIComponent(phone)}/reply`, { text: msg });
      toast.success("Risposta operatore inviata");
      setMsg("");
      await load();
      await openThread(phone);
    } catch { toast.error("Errore invio risposta"); }
    finally { setSending(false); }
  };



  const setConvStatus = async (p, status) => {
    await api.post(`/whatsapp/conversations/${encodeURIComponent(p)}/status`, { status });
    toast.success(status === "done" ? "Segnata come gestita" : "Segnata da fare");
    load();
  };

  const autoGenerateFaq = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post("/knowledge/auto-generate");
      toast.success(data.added ? `${data.added} FAQ aggiunte automaticamente` : "Nessuna nuova FAQ da aggiungere");
      load();
    } catch { toast.error("Errore nella generazione automatica"); }
    finally { setSuggesting(false); }
  };

  if (!settings) return null;

  const filteredConversations = conversations.filter((c) =>
    (c.contact_name || "").toLowerCase().includes(convSearch.toLowerCase()) || (c.phone || "").includes(convSearch)
  );

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-100">Assistente IA</h1>
          <p className="text-slate-400 text-sm mt-1">Gestione automatica delle richieste clienti · Claude Sonnet 4.6</p>
        </div>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 text-xs text-[#8B5CF6] font-medium">
          <Sparkles className="h-3.5 w-3.5" /> Risponde quando nessuna automazione si attiva
        </span>
      </div>

      {/* Chat con il cliente */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 bg-[#111827]/80 border border-[#1F2937] rounded-2xl overflow-hidden" data-testid="chat-view">
        {/* conversation list */}
        <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-[#1F2937] max-h-[520px] overflow-y-auto" data-testid="conversation-list">
          <div className="px-4 py-3 border-b border-[#1F2937] space-y-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-[#10B981]" />
              <span className="text-sm font-medium text-slate-200">Conversazioni ({conversations.length})</span>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
              <Input data-testid="conversation-search-input" value={convSearch} onChange={(e) => setConvSearch(e.target.value)}
                     placeholder="Cerca per numero o nome..." className="pl-8 h-8 text-xs bg-[#0A0E17] border-[#1F2937] text-slate-100" />
            </div>
          </div>
          {filteredConversations.length === 0 && (
            <p className="text-sm text-slate-500 p-4">Nessuna conversazione.</p>
          )}
          {filteredConversations.map((c, i) => (
            <button key={`${c.phone}-${i}`} onClick={() => openThread(c.phone)} data-testid={`conversation-${c.phone}`}
                    className={`w-full text-left px-4 py-3 border-b border-[#1F2937]/60 transition-colors ${activePhone === c.phone ? "bg-[#1F2937]/70" : "hover:bg-[#1F2937]/40"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-slate-200 font-medium truncate">{c.contact_name || c.phone}</span>
                {c.status === "todo" && <Badge className="bg-[#F59E0B]/20 text-[#F59E0B] border-0 text-xs shrink-0">Da fare</Badge>}
                {c.status === "done" && <Badge className="bg-[#10B981]/20 text-[#10B981] border-0 text-xs shrink-0">Gestita</Badge>}
              </div>
              <p className="text-xs text-slate-500 truncate mt-0.5">{c.last_message}</p>
            </button>
          ))}
        </div>

        {/* thread + composer */}
        <div className="lg:col-span-8 flex flex-col max-h-[520px]">
          {activePhone && (
            <div className="px-4 py-2 border-b border-[#1F2937] flex items-center justify-between gap-2 bg-[#0B1120]">
              <span className="text-sm text-slate-300 font-mono2 truncate">{activePhone}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" data-testid="status-todo-button" onClick={() => setConvStatus(activePhone, "todo")}
                        className="h-7 bg-[#1E293B] border-[#F59E0B]/30 text-[#F59E0B] text-xs">Da fare</Button>
                <Button size="sm" variant="outline" data-testid="status-done-button" onClick={() => setConvStatus(activePhone, "done")}
                        className="h-7 bg-[#1E293B] border-[#10B981]/30 text-[#10B981] text-xs">Gestita</Button>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[260px]" data-testid="chat-thread"
               style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect width='40' height='40' fill='%23E8EDF3'/%3E%3Ccircle cx='20' cy='20' r='1' fill='%231E4FD8' fill-opacity='0.08'/%3E%3C/svg%3E\")" }}>
            {!activePhone && <p className="text-sm text-slate-500 text-center py-16">Seleziona una conversazione o invia un messaggio per iniziare la chat.</p>}
            {activePhone && thread.length === 0 && <p className="text-sm text-slate-500 text-center py-16">Nessun messaggio.</p>}
            {thread.map((m) => (
              <div key={m.id} className="space-y-2">
                {m.message && (
                  <div className="flex items-start gap-2">
                    <div className="h-7 w-7 rounded-full bg-[#1E293B] flex items-center justify-center shrink-0"><UserRound className="h-4 w-4 text-slate-400" /></div>
                    <div className="bg-[#1E293B] rounded-2xl rounded-tl-sm px-3 py-2 max-w-[75%]">
                      <p className="text-sm text-slate-100 whitespace-pre-wrap">{m.message}</p>
                    </div>
                  </div>
                )}
                {m.ai_reply && (
                  <div className="flex items-start gap-2 justify-end">
                    <div className={`rounded-2xl rounded-tr-sm px-3 py-2 max-w-[75%] ${m.needs_human ? "bg-[#F59E0B]/15 border border-[#F59E0B]/30" : "bg-[#005C4B]"}`}>
                      <div className="mb-0.5">
                        {m.needs_human
                          ? <Badge className="bg-[#F59E0B]/20 text-[#F59E0B] border-0 text-xs"><Headset className="h-3 w-3 mr-1" />Operatore</Badge>
                          : <Badge className="bg-[#10B981]/20 text-[#10B981] border-0 text-xs"><Bot className="h-3 w-3 mr-1" />IA</Badge>}
                      </div>
                      <p className="text-sm text-slate-50 whitespace-pre-wrap">{m.ai_reply}</p>
                    </div>
                    <div className="h-7 w-7 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center shrink-0"><Bot className="h-4 w-4 text-[#8B5CF6]" /></div>
                  </div>
                )}
                {m.operator_reply && (
                  <div className="flex items-start gap-2 justify-end">
                    <div className="rounded-2xl rounded-tr-sm px-3 py-2 max-w-[75%] bg-[#1E293B] border border-[#10B981]/30">
                      <div className="mb-0.5"><Badge className="bg-[#10B981]/20 text-[#10B981] border-0 text-xs"><UserCog className="h-3 w-3 mr-1" />Operatore</Badge></div>
                      <p className="text-sm text-slate-100 whitespace-pre-wrap">{m.operator_reply}</p>
                    </div>
                    <div className="h-7 w-7 rounded-full bg-[#10B981]/20 flex items-center justify-center shrink-0"><UserCog className="h-4 w-4 text-[#10B981]" /></div>
                  </div>
                )}
                {!m.ai_reply && (m.action === "unsubscribed" || m.action === "subscribed") && (
                  <p className="text-center text-xs text-slate-500">
                    {m.action === "unsubscribed" ? "Cliente disiscritto (STOP)" : "Cliente re-iscritto (START)"}
                  </p>
                )}
              </div>
            ))}
          </div>
          <div className="border-t border-[#1F2937] p-3 space-y-2 bg-[#0B1120]">
            <Input data-testid="ai-sim-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
                   placeholder="+39..." className="bg-[#0A0E17] border-[#1F2937] text-slate-100 font-mono2 text-xs h-9" />
            <div className="flex gap-2">
              <Input data-testid="ai-sim-message-input" value={msg} onChange={(e) => setMsg(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                     placeholder="Scrivi come se fossi il cliente..." className="bg-[#0A0E17] border-[#1F2937] text-slate-100" />
              <Button onClick={send} disabled={sending} data-testid="ai-sim-send-button" title="Invia come cliente (risponde l'IA)"
                      className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-semibold shrink-0">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
              <Button onClick={sendOperator} disabled={sending} data-testid="operator-reply-button" variant="outline"
                      title="Rispondi come operatore"
                      className="bg-[#1E293B] border-[#10B981]/30 text-[#10B981] shrink-0">
                <UserCog className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-[11px] text-slate-500">Viola = messaggio cliente (risponde l'IA) · Verde = risposta manuale operatore</p>
          </div>
        </div>
      </div>

      {/* Config + Knowledge */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-6 space-y-4" data-testid="ai-config-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-xl bg-[#8B5CF6]/15 flex items-center justify-center"><Bot className="h-6 w-6 text-[#8B5CF6]" /></div>
              <h3 className="font-display text-lg font-semibold text-slate-200">Configurazione agente</h3>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={settings.ai_enabled} data-testid="ai-enabled-switch"
                      onCheckedChange={(v) => { if (!v || window.confirm("Attivare l'Assistente IA per questa azienda? Risponderà automaticamente ai messaggi in ingresso usando le informazioni attività qui sotto. Nessuna automazione o campagna verrà attivata.")) setSettings({ ...settings, ai_enabled: v }); }} />
              <span className="text-sm text-slate-300">{settings.ai_enabled ? "Attivo" : "Off"}</span>
            </div>
          </div>
          {!settings.ai_enabled && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="ai-disabled-banner">
              Assistente IA <b>disattivato</b>: i messaggi in ingresso vengono solo registrati in Chat. Compila nome e informazioni attività, poi attivalo con l'interruttore e salva (scelta riservata all'amministratore dell'azienda, tracciata nel registro operazioni).
            </div>
          )}
          <div className="space-y-2">
            <Label>Nome attività</Label>
            <Input data-testid="ai-business-name-input" value={settings.ai_business_name || ""}
                   onChange={(e) => setSettings({ ...settings, ai_business_name: e.target.value })}
                   className="bg-[#0A0E17] border-[#1F2937] text-slate-100" />
          </div>
          <div className="space-y-2">
            <Label>Informazioni attività (orari, sede, FAQ)</Label>
            <Textarea data-testid="ai-business-info-input" rows={6} value={settings.ai_business_info || ""}
                      onChange={(e) => setSettings({ ...settings, ai_business_info: e.target.value })}
                      className="bg-[#0A0E17] border-[#1F2937] text-slate-100 resize-none text-sm" />
          </div>
          <div className="flex items-center justify-between bg-[#0A0E17] rounded-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <Headset className="h-4 w-4 text-[#F59E0B]" />
              <span className="text-sm text-slate-300">Avvisami via email quando serve un operatore</span>
            </div>
            <Switch checked={settings.notify_operator} data-testid="notify-operator-switch"
                    onCheckedChange={(v) => setSettings({ ...settings, notify_operator: v })} />
          </div>
          <div className="flex items-center justify-between bg-[#0A0E17] rounded-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-[#8B5CF6]" />
              <span className="text-sm text-slate-300">FAQ automatiche (l'IA aggiunge da sola le più richieste)</span>
            </div>
            <Switch checked={settings.auto_faq} data-testid="auto-faq-switch"
                    onCheckedChange={(v) => setSettings({ ...settings, auto_faq: v })} />
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-ai-config-button"
                  className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva configurazione
          </Button>
        </div>

        <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-6" data-testid="knowledge-card">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-[#10B981]/15 flex items-center justify-center"><BookOpen className="h-6 w-6 text-[#10B981]" /></div>
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-200">Base conoscenza</h3>
              <p className="text-xs text-slate-500">FAQ e listini usati dall'IA ({knowledge.length} voci)</p>
            </div>
          </div>
          <Button type="button" onClick={() => setKbOpen(true)} data-testid="kb-add-document-button" className="w-full bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            <Plus className="h-4 w-4 mr-2" /> Aggiungi documento
          </Button>
          <p className="text-[11px] text-slate-500 mt-2">URL, file (PDF/Excel/CSV), inserimento manuale o FAQ proposte dalla cronologia chat. L'IA usa solo le fonti attive.</p>
          <Button type="button" onClick={autoGenerateFaq} disabled={suggesting} variant="outline" data-testid="kb-auto-generate-button"
                  className="w-full mt-2 bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/20">
            {suggesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Genera e aggiungi FAQ automaticamente ora
          </Button>
          <KnowledgeDialog open={kbOpen} onClose={() => setKbOpen(false)} knowledge={knowledge} onChanged={load} />
          <div className="space-y-2 max-h-48 overflow-y-auto mt-4" data-testid="knowledge-list">
            {knowledge.length === 0 && <p className="text-sm text-slate-500">Nessuna voce. Aggiungi FAQ o listini per risposte più ricche.</p>}
            {knowledge.map((k) => (
              <div key={k.id} className="bg-[#0A0E17] rounded-lg p-3 flex items-start justify-between gap-3" data-testid={`kb-item-${k.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#1E293B] text-slate-400 border-0 text-xs uppercase">{k.type}</Badge>
                    <span className={`text-sm font-medium truncate ${k.active === false ? "text-slate-500 line-through" : "text-slate-200"}`}>{k.title}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2 whitespace-pre-wrap">{k.content}</p>
                  {k.source_url && (
                    <div className="mt-1 space-y-0.5">
                      <a href={k.source_url} target="_blank" rel="noreferrer" data-testid={`kb-source-${k.id}`}
                         className="text-[11px] text-[#3B82F6] hover:underline break-all inline-flex items-center gap-1">
                        <Globe className="h-3 w-3" /> {k.source_url}
                      </a>
                      {k.last_synced_at && (
                        <p className="text-[10px] text-slate-500" data-testid={`kb-synced-${k.id}`}>Aggiornato {timeAgo(k.last_synced_at)}</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch checked={k.active !== false} onCheckedChange={async (v) => { await api.put(`/knowledge/${k.id}`, { active: v }); load(); }} data-testid={`kb-active-${k.id}`} title={k.active === false ? "Fonte disattivata (non usata dall'IA)" : "Fonte attiva"} className="data-[state=checked]:bg-[#10B981] scale-90" />
                  {k.source_url && (
                    <button onClick={() => refreshSource(k.id)} disabled={refreshingId === k.id} data-testid={`kb-refresh-${k.id}`}
                            title="Aggiorna ora" className="text-slate-500 hover:text-[#10B981]">
                      <RefreshCw className={`h-4 w-4 ${refreshingId === k.id ? "animate-spin" : ""}`} />
                    </button>
                  )}
                  <button onClick={() => deleteKnowledge(k.id)} data-testid={`kb-delete-${k.id}`} className="text-slate-500 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
